/**
 * Billing API client + tier predicates.
 *
 * Mirrors `backend/app/billing/models.py:BillingStatus`. Two-dimensional
 * subscription model:
 *
 *   - `subscriptionStatus`: where the user sits relative to Stripe
 *     (`free` / `active` / `past_due` / `canceled`).
 *   - `planLevel`: which paid tier when subscribed (`core` / `plus` /
 *     `unlimited`). Null for free or never-subscribed.
 *
 * Two orthogonal gate dimensions:
 *   - `gateType` — the KIND of wall: `"hard_wall"` (free tier past the
 *     first leak report — no further coaching, no reset), `"soft_cap"`
 *     (a paid tier's metered monthly cap, resets monthly), or `"none"`
 *     (uncapped: free pre-first-report, or unlimited).
 *   - `gatingWindow` — the resettable window of a soft cap + its
 *     `(used, limit)` triple: `"monthly"` (core 300 / plus 1000),
 *     `"daily"` (legacy free window, no longer produced), or `"none"`
 *     (free pre-report, the free hard wall with `limit: 0`, or unlimited).
 *
 * The free tier is a HARD WALL at the first leak report: pre-25 signals
 * uncapped, then `over_limit` permanently with `limit: 0` (so
 * `isOverLimit` locks the drill UI) and `gateType: "hard_wall"` (so the
 * UI shows subscribe-to-continue copy with NO countdown).
 *
 * Signal-count fields drive the first-leak-report celebration modal
 * and the "X / 25 signals" progress chip shown to free pre-cap users.
 *
 * See root `CLAUDE.md` "Architecture: subscription billing" for the
 * full contract.
 */

import { backendUrl } from "@/shared/config";
import { loadTokens } from "@/features/auth/lib/tokenStorage";

export type SubscriptionStatus =
  | "free"
  | "active"
  | "past_due"
  | "canceled";

export type PlanLevel = "core" | "plus" | "unlimited";
export type Plan = "monthly" | "annual";
export type GatingWindow = "daily" | "monthly" | "none";
/** The KIND of wall a user faces — distinguishes the free hard wall
 *  (no reset, subscribe-to-continue) from a paid soft cap (resets). */
export type GateType = "soft_cap" | "hard_wall" | "none";

/** One currently-active hard cap. `used`/`limit` are carried for the
 *  client to derive a qualitative level (ok/near/over) — surfaces still
 *  must NOT render the raw `used` number (see billing CLAUDE.md). The
 *  Settings usage card uses this to show daily + monthly side by side. */
export interface CapWindow {
  window: "daily" | "monthly";
  used: number;
  limit: number;
}

export type CapLevel = "ok" | "near" | "over";

/** Qualitative fill level for a cap — 80% = near, at/over limit = over.
 *  Mirrors the isNearLimit/isOverLimit thresholds for the single window. */
export function capLevel(cap: CapWindow): CapLevel {
  if (cap.limit <= 0) return "ok";
  if (cap.used >= cap.limit) return "over";
  if (cap.used >= 0.8 * cap.limit) return "near";
  return "ok";
}

/** User-facing display name for each paid tier. Single source of truth
 *  so Settings + UsageChip + Pricing don't each hardcode the mapping. */
export const PLAN_LABELS: Record<PlanLevel, string> = {
  core: "Core",
  plus: "Plus",
  unlimited: "Unlimited",
};

export interface BillingStatus {
  subscriptionStatus: SubscriptionStatus;
  planLevel: PlanLevel | null;
  plan: Plan | null;
  isFounder: boolean;
  currentPeriodEnd: string | null;

  // First-leak-report milestone
  signalCount: number;
  signalsForFirstReport: number; // 25
  firstLeakReportMilestoneShown: boolean;

  // Onboarding welcome modal — fires once on first sign-in
  onboardingWelcomeShown: boolean;

  // Active gating window (the single binding cap)
  gatingWindow: GatingWindow;
  used: number;
  limit: number | null;

  // The KIND of wall (hard_wall / soft_cap / none) — see GateType. Drives
  // the no-countdown subscribe copy for the free hard wall vs the
  // reset-countdown copy for a paid soft cap.
  gateType: GateType;

  // All currently-active hard caps (for the Settings usage card). Empty
  // for uncapped users AND for the free hard wall (no meter to show);
  // core/plus carry the single monthly cap.
  caps: CapWindow[];

  billingEnabled: boolean;
}

interface RawBillingStatus {
  subscription_status: SubscriptionStatus;
  plan_level: PlanLevel | null;
  plan: Plan | null;
  is_founder: boolean;
  current_period_end: string | null;
  signal_count: number;
  signals_for_first_report: number;
  first_leak_report_milestone_shown: boolean;
  onboarding_welcome_shown: boolean;
  gating_window: GatingWindow;
  used: number;
  limit: number | null;
  // Optional inbound: a backend that hasn't deployed the gate_type/caps
  // fields yet (deploy-order skew) defaults via the mapper (?? "none" / []).
  gate_type?: GateType;
  caps?: CapWindow[];
  billing_enabled: boolean;
}

/** Convert the backend wire shape into the camelCase TS shape.
 *  Exported for the test file. */
export function mapBillingStatus(raw: RawBillingStatus): BillingStatus {
  return {
    subscriptionStatus: raw.subscription_status,
    planLevel: raw.plan_level,
    plan: raw.plan,
    isFounder: raw.is_founder,
    currentPeriodEnd: raw.current_period_end,
    signalCount: raw.signal_count,
    signalsForFirstReport: raw.signals_for_first_report,
    firstLeakReportMilestoneShown: raw.first_leak_report_milestone_shown,
    onboardingWelcomeShown: raw.onboarding_welcome_shown,
    gatingWindow: raw.gating_window,
    used: raw.used,
    limit: raw.limit,
    gateType: raw.gate_type ?? "none",
    caps: raw.caps ?? [],
    billingEnabled: raw.billing_enabled,
  };
}

// ----------------------------------------------------------------------------
// HTTP helpers (small enough to keep self-contained; not worth re-exporting
// authedFetch since these endpoints have simple shapes)
// ----------------------------------------------------------------------------

async function authedRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) throw new Error("Not signed in");
  const res = await fetch(backendUrl(path), {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${tokens.accessToken}`,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Billing request failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

/** Current billing state for the signed-in user. Cheap; safe to call on
 *  every Settings mount + every Pricing-page mount + as the source of
 *  truth for the AppShell UsageChip + NearCapBanner + FirstLeakReportModal
 *  via TanStack Query (key: ["billing-status"]). */
export async function getBillingStatus(): Promise<BillingStatus> {
  const raw = await authedRequest<RawBillingStatus>("/billing/status");
  return mapBillingStatus(raw);
}

/** Kick off a Stripe Checkout Session. Server picks the Stripe Price
 *  ID from the (planLevel, plan) pair. Returns the hosted URL — caller
 *  does `window.location.href = url`. */
export async function createCheckoutSession(args: {
  planLevel: PlanLevel;
  plan: Plan;
  promoCode?: string;
}): Promise<{ url: string }> {
  return authedRequest<{ url: string }>("/billing/checkout-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan_level: args.planLevel,
      plan: args.plan,
      promo_code: args.promoCode || null,
    }),
  });
}

/** Fire a client-side conversion-funnel event (best-effort telemetry).
 *  Only pure-UI impression events are accepted server-side (currently
 *  `pricing_viewed`); the server-authoritative funnel events (wall_hit,
 *  checkout_started) are recorded on the backend, not here. Fire-and-forget
 *  — swallows errors so a telemetry hiccup never disrupts the page. */
export async function recordFunnelEvent(event: string): Promise<void> {
  try {
    await authedRequest<{ recorded: boolean }>("/funnel/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event }),
    });
  } catch {
    // telemetry is best-effort — never surface to the UI
  }
}

/** Open a Stripe Customer Portal session (cancel / payment / invoices /
 *  plan change). Returns the hosted URL. */
export async function createPortalSession(): Promise<{ url: string }> {
  return authedRequest<{ url: string }>("/billing/portal-session", {
    method: "POST",
  });
}

/** Tell the backend that the user has seen the first-leak-report
 *  celebration. Backend flips the durable flag so the modal stops
 *  showing on subsequent /billing/status reads. */
export async function acknowledgeFirstLeakReportMilestone(): Promise<void> {
  await authedRequest<{ acknowledged: boolean }>(
    "/billing/acknowledge-first-leak-report-milestone",
    { method: "POST" },
  );
}

/** Tell the backend that the user has dismissed the onboarding welcome
 *  modal. Backend flips the durable flag so the modal never re-fires. */
export async function acknowledgeOnboardingWelcome(): Promise<void> {
  await authedRequest<{ acknowledged: boolean }>(
    "/billing/acknowledge-onboarding-welcome",
    { method: "POST" },
  );
}

// ----------------------------------------------------------------------------
// Derived predicates — pure functions over BillingStatus. Exported so UI
// surfaces (UsageChip color, Drills CappedGate, NearCapBanner threshold)
// don't duplicate the threshold math.
// ----------------------------------------------------------------------------

/** True iff the free user has hit the post-first-report HARD WALL:
 *  no further coaching, no reset — subscribe to continue. Distinct from
 *  a paid soft cap (which resets). Drives the no-countdown copy. */
export function isHardWall(s: BillingStatus): boolean {
  return s.gateType === "hard_wall";
}

/** True if the user has a wall they can hit. Pre-25 free users and
 *  unlimited subscribers don't; the free hard wall does (even though its
 *  `gatingWindow` is "none" — the wall is binary, not windowed). */
export function isCappedTier(s: BillingStatus): boolean {
  return s.gatingWindow !== "none" || isHardWall(s);
}

/** True iff the user is at or over their limit / wall. The /chat
 *  endpoint already returns 402 in this case; UI surfaces use this to
 *  render a locked experience BEFORE the user tries to chat. The hard
 *  wall reports `limit: 0` so the numeric check already catches it, but
 *  we check `gateType` too for clarity + deploy-skew safety. */
export function isOverLimit(s: BillingStatus): boolean {
  if (isHardWall(s)) return true;
  if (s.limit == null) return false;
  return s.used >= s.limit;
}

/** "Approaching the limit" — 80% threshold within the active window.
 *  Returns false if there's no active cap (window === "none"). */
export function isNearLimit(s: BillingStatus): boolean {
  if (s.limit == null || s.limit <= 0) return false;
  if (isOverLimit(s)) return false;
  return s.used >= 0.8 * s.limit;
}

/** Should the first-leak-report celebration modal fire?
 *  Yes iff the user has reached the signal threshold AND hasn't yet
 *  acknowledged the milestone.
 *
 *  NOTE: deliberately does NOT gate on `onboardingWelcomeShown`. The
 *  welcome modal is a blocking overlay rendered ABOVE this one (later
 *  in the App.tsx provider tree), so if both fire at once the user sees
 *  welcome first and the leak report is revealed when they dismiss it.
 *  An earlier `&& onboardingWelcomeShown` guard caused a stuck state for
 *  a tester who started a session on a pre-welcome bundle (mid-deploy
 *  skew): his `onboarding_welcome_shown` stayed false, so the guard
 *  permanently suppressed his leak-report modal. */
export function shouldShowFirstLeakReportCelebration(s: BillingStatus): boolean {
  return (
    s.signalCount >= s.signalsForFirstReport &&
    !s.firstLeakReportMilestoneShown
  );
}

/** Should the onboarding welcome modal fire?
 *  Yes iff the user hasn't yet dismissed it. Mounted in App.tsx and
 *  auto-fires from the billing-status query once the user is signed in. */
export function shouldShowWelcomeModal(s: BillingStatus): boolean {
  return !s.onboardingWelcomeShown;
}
